import { compact, find, findIndex, map } from 'es-toolkit/compat';
import { SectionTable, SessionDataset, SessionDatasetJoinCandidate } from 'interfaces/agentSession';

const UNDERSCORE_REGEX = /_/g;

// Only map primitive types currently supported by our preview grid definition.
const toTableColumnType = (dataType: SessionDataset['columns'][number]['dataType']) => {
  if (dataType === 'number' || dataType === 'boolean' || dataType === 'date') {
    return dataType;
  }

  return undefined;
};

// Build a generic section-table shape consumed by shared study-report table helpers.
export const buildPreviewTable = (dataset: SessionDataset): SectionTable => ({
  id: `dataset-preview-${dataset.id}`,
  title: dataset.metadata.displayName || dataset.fileName,
  description: dataset.metadata.description || null,
  columns: map(dataset.columns, (column) => ({
    field: column.name,
    headerName: column.name,
    type: toTableColumnType(column.dataType),
  })),
  rows: dataset.rows,
  sourceType: 'session_dataset',
  sourceConfig: { datasetId: dataset.id },
  generatedAt: dataset.updatedAt,
});

export const formatMatchedCaseField = (matchedCaseField: SessionDatasetJoinCandidate['matchedCaseField']) =>
  matchedCaseField.replace(UNDERSCORE_REGEX, ' ');

// Prefer the backend-selected primary candidate when join analysis is available.
export const getPrimaryJoinCandidate = (dataset: SessionDataset) =>
  find(dataset.joinAnalysis?.candidateMatches, (candidate) => candidate.isPrimary) || null;

// Upsert mutation result into cached dataset list while preserving other items.
export const upsertDatasetInCache = (
  currentDatasets: SessionDataset[] | undefined,
  nextDataset: SessionDataset
): SessionDataset[] => {
  const existingDatasets = currentDatasets || [];
  const existingIndex = findIndex(existingDatasets, (dataset) => dataset.id === nextDataset.id);

  if (existingIndex === -1) {
    return [...existingDatasets, nextDataset];
  }

  return map(existingDatasets, (dataset) => (dataset.id === nextDataset.id ? nextDataset : dataset));
};

// Remove a dataset from cache by id; compact is used to strip null placeholders.
export const removeDatasetFromCache = (
  currentDatasets: SessionDataset[] | undefined,
  datasetId: string
): SessionDataset[] =>
  compact(map(currentDatasets, (dataset) => (dataset.id === datasetId ? null : dataset))) as SessionDataset[];
